import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { FaUserCircle } from "react-icons/fa";
import { FiChevronDown, FiMenu, FiX } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";

const PUBLIC_LINKS = [
  { to: "/roster", label: "Talent" },
  { to: "/events", label: "Events" },
  { to: "/concierge", label: "Concierge" },
  { to: "/submit", label: "Submit" },
  { to: "/affiliate/stats", label: "Affiliate" },
  { to: "/community", label: "Community" },
  { to: "/brands", label: "Our Brands" },
];

export const ADMIN_LINKS = [
  { to: "/admin/highlights", label: "Homepage Highlights" },
  { to: "/ticket/scan", label: "Ticket Scan" },
  { to: "/door-purchase", label: "Door Purchase" },
  { to: "/admin/events/create", label: "Create Event" },
  { to: "/admin/events", label: "Manage Events" },
  { to: "/admin/external-events", label: "External Events" },
  { to: "/admin/awin-links", label: "Awin Links" },
  { to: "/admin/concierge", label: "Concierge CRM" },
  { to: "/admin/submissions", label: "Submissions" },
  { to: "/admin/destinations", label: "Destinations" },
  { to: "/admin/roster", label: "Roster Admin" },
  { to: "/admin/team", label: "Team Admin" },
  { to: "/admin/articles", label: "Articles" },
  { to: "/admin/adminTop5Editor", label: "Top 5 Edit" },
  { to: "/admin/benefits", label: "Benefits" },
  { to: "/admin/verification", label: "Sub Verify" },
];

export const MASTER_LINKS = [
  { to: "/admin/external-sales", label: "External Sales" },
  { to: "/master/stats", label: "Master Stats" },
  { to: "/master/user", label: "Master Roles" },
  { to: "/master-tickets", label: "Master Tickets" },
];

export default function Navbar() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const adminRef = useRef(null);
  const staffRef = useRef(null);

  const role = user?.role;
  const isAdmin = role === "ADMIN";
  const isMaster = role === "MASTER";
  const isAdminOrMaster = isAdmin || isMaster;
  const isEventWorker = role === "EVENT_WORKER";
  const showAffiliate = token && ["AFFILIATE", "ADMIN", "MASTER"].includes(role);

  useEffect(() => {
    const onDown = (event) => {
      if (adminRef.current && !adminRef.current.contains(event.target)) setAdminOpen(false);
      if (staffRef.current && !staffRef.current.contains(event.target)) setStaffOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  const closeMenus = () => {
    setOpen(false);
    setAdminOpen(false);
    setStaffOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    closeMenus();
    navigate("/login");
  };

  const visiblePublicLinks = PUBLIC_LINKS.filter((link) => {
    if (isEventWorker) return false;
    if (link.label === "Affiliate") return showAffiliate || !token;
    return true;
  });

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#080808]/85 backdrop-blur-xl">
      <nav className="container relative flex min-h-16 items-center justify-between gap-6 py-2">
        <Link to="/" className="flex min-w-[8rem] shrink-0 items-center gap-3" onClick={closeMenus}>
          <img
            src="/HEY_LETS_TRAVEL_LOGO.png"
            alt="HeyLetsTravel logo"
            className="h-11 w-auto object-contain"
            loading="eager"
            decoding="async"
            draggable={false}
          />
          <span className="font-display text-xl tracking-[0.18em] text-brand-500">
            HLT
          </span>
        </Link>

        <div className="hidden lg:flex items-center justify-center gap-1">
          {visiblePublicLinks.map((link) => (
            <TopLink key={link.to} to={link.to} onClick={closeMenus}>
              {link.label}
            </TopLink>
          ))}

          {token && isEventWorker && (
            <div className="relative" ref={staffRef}>
              <button
                type="button"
                className="nav-link inline-flex items-center gap-1 whitespace-nowrap"
                onClick={() => {
                  setStaffOpen((value) => !value);
                  setAdminOpen(false);
                }}
              >
                Staff <FiChevronDown />
              </button>
              {staffOpen && (
                <Dropdown align="left">
                  <MenuLink to="/ticket/scan" onClick={closeMenus}>Ticket Scan</MenuLink>
                  <MenuLink to="/door-purchase" onClick={closeMenus}>Door Purchase</MenuLink>
                </Dropdown>
              )}
            </div>
          )}

          {token && isAdminOrMaster && (
            <div className="relative" ref={adminRef}>
              <button
                type="button"
                className="nav-link inline-flex items-center gap-1 whitespace-nowrap"
                onClick={() => {
                  setAdminOpen((value) => !value);
                  setStaffOpen(false);
                }}
              >
                Admin <FiChevronDown />
              </button>
              {adminOpen && (
                <Dropdown align="right" wide>
                  <div className="grid grid-cols-2 gap-2">
                    {ADMIN_LINKS.map((link) => (
                      <MenuLink key={link.to} to={link.to} onClick={closeMenus}>
                        {link.label}
                      </MenuLink>
                    ))}
                    {isMaster &&
                      MASTER_LINKS.map((link) => (
                        <MenuLink key={link.to} to={link.to} onClick={closeMenus}>
                          {link.label}
                        </MenuLink>
                      ))}
                  </div>
                </Dropdown>
              )}
            </div>
          )}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          {!token ? (
            <>
              <Link to="/login" className="btn-secondary">Login</Link>
              <Link to="/register" className="btn-secondary">Register</Link>
              <Link to="/subscribe" className="btn-primary">Go Pro</Link>
            </>
          ) : (
            <>
              {!isEventWorker && <Link to="/subscribe" className="btn-primary">Go Pro</Link>}
              {!isEventWorker && (
                <Link to="/account" title="Account" className="rounded-lg p-2 text-slate-300 hover:bg-white/10 hover:text-white">
                  <FaUserCircle className="text-2xl" />
                </Link>
              )}
              <button onClick={handleLogout} className="btn-secondary">Logout</button>
            </>
          )}
        </div>

        <button
          type="button"
          className="btn-ghost lg:hidden p-2"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#080808]/95">
          <div className="container py-3">
            {!isEventWorker &&
              visiblePublicLinks.map((link) => (
                <MobileLink key={link.to} to={link.to} onClick={closeMenus}>
                  {link.label}
                </MobileLink>
              ))}

            {token && isEventWorker && (
              <MenuGroup title="Staff">
                <MobileLink to="/ticket/scan" onClick={closeMenus}>Ticket Scan</MobileLink>
                <MobileLink to="/door-purchase" onClick={closeMenus}>Door Purchase</MobileLink>
              </MenuGroup>
            )}

            {token && isAdminOrMaster && (
              <MenuGroup title="Admin">
                {ADMIN_LINKS.map((link) => (
                  <MobileLink key={link.to} to={link.to} onClick={closeMenus}>
                    {link.label}
                  </MobileLink>
                ))}
                {isMaster &&
                  MASTER_LINKS.map((link) => (
                    <MobileLink key={link.to} to={link.to} onClick={closeMenus}>
                      {link.label}
                    </MobileLink>
                  ))}
              </MenuGroup>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-white/10 pt-3">
              {!token ? (
                <>
                  <Link to="/login" className="btn-secondary" onClick={closeMenus}>Login</Link>
                  <Link to="/register" className="btn-secondary" onClick={closeMenus}>Register</Link>
                  <Link to="/subscribe" className="btn-primary col-span-2" onClick={closeMenus}>Go Pro</Link>
                </>
              ) : (
                <>
                  {!isEventWorker && <Link to="/account" className="btn-secondary" onClick={closeMenus}>Account</Link>}
                  {!isEventWorker && <Link to="/subscribe" className="btn-primary" onClick={closeMenus}>Go Pro</Link>}
                  <button onClick={handleLogout} className="btn-secondary col-span-2">Logout</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function TopLink({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `nav-link whitespace-nowrap ${isActive ? "nav-link-active" : ""}`
      }
      onClick={onClick}
    >
      {children}
    </NavLink>
  );
}

function MobileLink({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `block rounded-lg px-3 py-2 text-sm font-medium ${
          isActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`
      }
      onClick={onClick}
    >
      {children}
    </NavLink>
  );
}

function MenuLink({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `block rounded-lg px-3 py-2 text-sm ${
          isActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`
      }
      onClick={onClick}
    >
      {children}
    </NavLink>
  );
}

function Dropdown({ children, align = "right", wide = false }) {
  return (
    <div
      className={`absolute ${align === "right" ? "right-0" : "left-0"} mt-2 ${
        wide ? "w-[34rem]" : "w-56"
      } rounded-2xl border border-white/10 bg-[var(--surface)] p-3 text-slate-100 shadow-card ring-1 ring-black/20`}
    >
      {children}
    </div>
  );
}

function MenuGroup({ title, children }) {
  return (
    <div className="mt-3 border-t border-white/10 pt-3">
      <div className="px-3 pb-1 text-[11px] uppercase tracking-wider text-slate-500">
        {title}
      </div>
      {children}
    </div>
  );
}

